/**
 * Frame Service
 * -------------
 * Handles frame image fetching using apiClient (same auth as all other services).
 *
 * Sources of valid frame IDs — NEVER invent them client-side:
 *   1. session.data.first_frame_id        → thumbnail/preview
 *   2. GET /frames?session_id=X           → list of available frame IDs
 *   3. detection.evidence_frame_ids       → frames tied to specific TTPs (best for Analysis)
 */

import { apiClient, getAuthenticatedImageUrl } from "./apiClient";

// ---------------------------------------------------------------------------
// Image loading
// ---------------------------------------------------------------------------

/**
 * Load a single frame as a blob URL usable in <img src>.
 * Uses apiClient so auth headers are handled automatically.
 *
 * @param {string} sessionId
 * @param {number} frameId
 * @returns {Promise<string|null>} blob URL or null on failure
 */
export async function loadFrameImage(sessionId, frameId) {
  return getAuthenticatedImageUrl(
    `/sessions/${sessionId}/frames/${frameId}.png`,
    { silent: true }
  );
}

// ---------------------------------------------------------------------------
// Frame discovery — always consult API, never invent IDs
// ---------------------------------------------------------------------------

/**
 * Fetch list of available frames for a session.
 * Returns array of frame objects: { frame_id, timestamp_seconds, risk_score, ... }
 *
 * @param {string} sessionId
 * @param {number} limit
 * @returns {Promise<Array>}
 */
export async function getSessionFrames(sessionId, limit = 50) {
  try {
    const res = await apiClient(`/frames?session_id=${sessionId}&limit=${limit}`);
    return res?.data ?? [];
  } catch (err) {
    console.error("[frameService] getSessionFrames failed:", err);
    return [];
  }
}

/**
 * Find the closest available frame to a given timestamp (in seconds).
 * Useful when you need a frame "near" a specific event time.
 *
 * @param {Array} availableFrames  - output of getSessionFrames()
 * @param {number} targetSeconds   - desired timestamp in seconds
 * @returns {object|null}          - closest frame object or null
 */
export function findClosestFrame(availableFrames, targetSeconds) {
  if (!availableFrames?.length) return null;

  return availableFrames.reduce((closest, frame) => {
    const frameDelta = Math.abs((frame.timestamp_seconds ?? 0) - targetSeconds);
    const closestDelta = Math.abs((closest.timestamp_seconds ?? 0) - targetSeconds);
    return frameDelta < closestDelta ? frame : closest;
  });
}

// ---------------------------------------------------------------------------
// Memory management
// ---------------------------------------------------------------------------

/**
 * Revoke a single blob URL.
 * @param {string} url
 */
export function revokeFrameImageUrl(url) {
  if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
}

/**
 * Revoke all blob URLs in a Map to prevent memory leaks.
 * @param {Map<number, string>} imageMap
 */
export function revokeFrameImages(imageMap) {
  imageMap?.forEach((url) => revokeFrameImageUrl(url));
}